import { Link, useLocation } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { subflowHref } from "../lib/subflow";

export function subflowNeighbors(items, focusId) {
  const flow = items.filter(({ id }) => id !== "overview");
  const index = flow.findIndex(({ id }) => id === focusId);
  if (index < 0) return { prev: null, next: null, index: -1, total: flow.length };
  return {
    prev: index > 0 ? flow[index - 1] : null,
    next: index < flow.length - 1 ? flow[index + 1] : null,
    index,
    total: flow.length,
  };
}

export default function SubFlowNav({ items, focus, appId, testId = "subflow-nav", param = "focus", buildHref }) {
  const location = useLocation();
  const pathname = location.pathname;

  return (
    <nav className="subflow-nav" data-testid={testId} aria-label="Sub-flow">
      {items.map(({ id, label }) => {
        const active = focus === id;
        const to = buildHref ? buildHref(id) : subflowHref(pathname, appId, id, param);
        return (
          <Link
            key={id}
            to={to}
            className={active ? "subflow-pill on" : "subflow-pill"}
            data-testid={`subflow-${id}`}
            aria-current={active ? "page" : undefined}
          >
            {label}
          </Link>
        );
      })}
    </nav>
  );
}

export function SubFlowPager({ items, focus, appId, testId = "subflow-pager", param = "focus", buildHref }) {
  const location = useLocation();
  const pathname = location.pathname;
  const { prev, next } = subflowNeighbors(items, focus);
  if (!prev && !next) return null;

  const hrefFor = (item) => (buildHref ? buildHref(item.id) : subflowHref(pathname, appId, item.id, param));

  return (
    <div className="subflow-pager" data-testid={testId}>
      {prev ? (
        <Link
          to={hrefFor(prev)}
          className="secondary-button"
          data-testid="subflow-back"
        >
          <ChevronLeft size={16} />
          Back · {prev.label}
        </Link>
      ) : (
        <span className="subflow-pager-spacer" aria-hidden="true" />
      )}
      {next ? (
        <Link
          to={hrefFor(next)}
          className="primary-button"
          data-testid="subflow-forward"
        >
          Next · {next.label}
          <ChevronRight size={16} />
        </Link>
      ) : (
        <span className="subflow-pager-spacer" aria-hidden="true" />
      )}
    </div>
  );
}

export function FocusPanel({ kicker, title, detail, badge, children, testId }) {
  return (
    <section className="focus-panel" data-testid={testId}>
      <div className="focus-panel-head">
        <div>
          {kicker && <p className="kicker">{kicker}</p>}
          <h2>{title}</h2>
          {detail && <p className="muted small-note">{detail}</p>}
        </div>
        {badge}
      </div>
      {children}
    </section>
  );
}

export function MetricStrip({ items }) {
  if (!items?.length) return null;
  return (
    <div className="focus-metrics">
      {items.map((item) => (
        <div className="focus-metric" data-testid={item.testId} key={item.label}>
          <small>{item.label}</small>
          <strong>{item.value}</strong>
          {item.detail && <span>{item.detail}</span>}
        </div>
      ))}
    </div>
  );
}

export function SimpleBars({ rows, testId = "simple-bars" }) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <div className="simple-bars" data-testid={testId}>
      {rows.map((row) => (
        <div className="simple-bar-row" key={row.label} data-testid={row.testId}>
          <span>{row.label}</span>
          <span className="simple-bar-wrap">
            <span className={`simple-bar tone-${row.tone || "cyan"}`} style={{ width: `${(row.value / max) * 100}%` }} />
          </span>
          <b>{row.display ?? row.value}</b>
        </div>
      ))}
    </div>
  );
}
